import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../Assets/assetsShared";
import { Badges, fmtDate } from "./ReviewParts";
import "./EmployeeReview.css";

const EMPTY_GOAL = {
  title: "",
  description: "",
  targetDate: "",
  shared: false,
};

const initialOf = (name) =>
  String(name || "?")
    .trim()
    .charAt(0)
    .toUpperCase() || "?";

export default function MyReview() {
  const [data, setData] = useState({
    canGive: false,
    counts: { positive: 0, improvement: 0, achieved: 0, pending: 0 },
    reviews: [],
    goals: [],
  });
  const [notes, setNotes] = useState({}); // reviewId -> text being typed
  const [editingNote, setEditingNote] = useState(null); // reviewId being edited
  const [goalForm, setGoalForm] = useState(EMPTY_GOAL);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/employee-reviews/mine");
      setData(res.data);
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not load your review.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* ---------- my update on a review ---------- */
  const startEditNote = (review) => {
    setNotes((prev) => ({ ...prev, [review._id]: review.employeeNote || "" }));
    setEditingNote(review._id);
  };

  const cancelEditNote = (reviewId) => {
    setEditingNote(null);
    setNotes((prev) => {
      const next = { ...prev };
      delete next[reviewId];
      return next;
    });
  };

  const saveNote = async (review) => {
    const text = (notes[review._id] ?? review.employeeNote ?? "").trim();

    if (!text && !review.employeeNote) {
      setMessage("Write your update first.");
      return;
    }

    try {
      await api.patch(`/employee-reviews/mine/${review._id}/note`, {
        note: text,
      });
      setMessage(
        text
          ? "Your update is saved. Admin and HR can see it."
          : "Your update is removed.",
      );
      cancelEditNote(review._id);
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not save your update.");
    }
  };

  /* ---------- goals ---------- */
  const addGoal = async () => {
    if (!goalForm.title.trim()) {
      setMessage("Enter the goal.");
      return;
    }

    try {
      await api.post("/employee-reviews/goals", goalForm);
      setMessage(
        goalForm.shared ? "Goal added and shared with admin." : "Goal added.",
      );
      setGoalForm(EMPTY_GOAL);
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not add the goal.");
    }
  };

  const updateGoal = async (id, patch, okText) => {
    try {
      await api.put(`/employee-reviews/goals/${id}`, patch);
      setMessage(okText);
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not update the goal.");
    }
  };

  const removeGoal = async (id) => {
    if (!window.confirm("Delete this goal?")) return;

    try {
      await api.delete(`/employee-reviews/goals/${id}`);
      setMessage("Goal deleted.");
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not delete the goal.");
    }
  };

  const c = data.counts;

  return (
    <div className="er-page">
      {/* ----- banner ----- */}
      <header className="er-banner">
        <div>
          <h1>My Review</h1>
          <p>
            See what the management says about your work, and share your own
            goals.
          </p>

          {data.canGive && (
            <Link className="er-banner-link" to="/employee-reviews/give">
              Give Reviews to Employees
            </Link>
          )}
        </div>

        <div className="er-stats">
          <div className="er-stat">
            <strong>{c.positive}</strong>
            <span>Positive</span>
          </div>
          <div className="er-stat">
            <strong>{c.improvement}</strong>
            <span>Improvement</span>
          </div>
          <div className="er-stat">
            <strong>{c.achieved}</strong>
            <span>Achieved</span>
          </div>
          <div className="er-stat">
            <strong>{c.pending}</strong>
            <span>Pending</span>
          </div>
        </div>
      </header>

      {message && <div className="er-message">{message}</div>}

      {/* ================= REVIEWS FROM MANAGEMENT ================= */}
      <section className="er-card">
        <div className="er-list-head">
          <h3>Reviews from Management</h3>
          <Badges counts={c} />
        </div>

        {loading ? (
          <div className="er-empty">Loading...</div>
        ) : data.reviews.length === 0 ? (
          <div className="er-empty">No reviews yet.</div>
        ) : (
          data.reviews.map((r) => {
            const good = r.points.filter((p) => p.type === "positive");
            const improve = r.points.filter((p) => p.type === "improvement");
            const editing = editingNote === r._id;

            return (
              <article className="er-review" key={r._id}>
                <div className="er-review-head">
                  <div className="er-who">
                    <span className="er-avatar">
                      {initialOf(r.createdByName)}
                    </span>
                    <div>
                      <strong>Review from {r.createdByName}</strong>
                      <span className="er-muted">{fmtDate(r.createdAt)}</span>
                    </div>
                  </div>
                </div>

                {(good.length > 0 || improve.length > 0) && (
                  <div className="er-two">
                    {good.length > 0 && (
                      <div className="er-panel er-panel-pos">
                        <h4 className="er-h-pos">You are doing well in</h4>
                        <div className="er-points">
                          {good.map((p) => (
                            <span
                              key={p._id}
                              className="er-point er-point-positive"
                            >
                              <b>{p.name}</b>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {improve.length > 0 && (
                      <div className="er-panel er-panel-imp">
                        <h4 className="er-h-imp">Needs improvement</h4>

                        {improve.map((p) => (
                          <div className="er-imp-row" key={p._id}>
                            <span className="er-point er-point-improvement">
                              <b>{p.name}</b>
                            </span>

                            <em className={`er-status ${p.status}`}>
                              {p.status === "achieved"
                                ? `Achieved${
                                    p.achievedAt
                                      ? " on " + fmtDate(p.achievedAt)
                                      : ""
                                  }`
                                : "Pending"}
                            </em>
                          </div>
                        ))}

                        <p className="er-note">
                          Admin or HR marks a point as achieved.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {r.suggestion && (
                  <div className="er-bubble er-bubble-mgmt">
                    <div className="er-bubble-head">
                      <b>Suggestion from {r.createdByName}</b>
                    </div>
                    <p>{r.suggestion}</p>
                  </div>
                )}

                {/* ----- my update: bubble after saving, box only to write ----- */}
                {r.employeeNote && !editing ? (
                  <div className="er-bubble er-bubble-me">
                    <div className="er-bubble-head">
                      <b>My update</b>
                      {r.employeeNoteAt && (
                        <span className="er-muted">
                          {fmtDate(r.employeeNoteAt)}
                        </span>
                      )}
                      <button
                        type="button"
                        className="er-link-btn"
                        onClick={() => startEditNote(r)}
                      >
                        Edit
                      </button>
                    </div>
                    <p>{r.employeeNote}</p>
                  </div>
                ) : (
                  <div className="er-composer">
                    <label>
                      My update{" "}
                      <span className="er-muted">
                        (admin and HR can see it)
                      </span>
                    </label>

                    <textarea
                      placeholder="Write what you did to improve, or any comment..."
                      value={notes[r._id] ?? ""}
                      onChange={(e) =>
                        setNotes((prev) => ({
                          ...prev,
                          [r._id]: e.target.value,
                        }))
                      }
                    />

                    <div className="er-composer-actions">
                      {editing && (
                        <button
                          type="button"
                          className="er-btn er-btn-small er-btn-light"
                          onClick={() => cancelEditNote(r._id)}
                        >
                          Cancel
                        </button>
                      )}

                      <button
                        type="button"
                        className="er-btn er-btn-small"
                        onClick={() => saveNote(r)}
                      >
                        {r.employeeNote ? "Update" : "Send Update"}
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })
        )}
      </section>

      {/* ================= MY GOALS ================= */}
      <section className="er-card">
        <h3>My Goals</h3>
        <p className="er-hint">
          Goals are private. Tick "Share with admin" to let admin and HR see a
          goal. Their reply appears under the goal.
        </p>

        <div className="er-goal-form">
          <label className="er-field">
            <span>Goal</span>
            <input
              placeholder="e.g. Learn Revit families"
              value={goalForm.title}
              onChange={(e) =>
                setGoalForm((f) => ({ ...f, title: e.target.value }))
              }
            />
          </label>

          <label className="er-field">
            <span>Target date</span>
            <input
              type="date"
              value={goalForm.targetDate}
              onChange={(e) =>
                setGoalForm((f) => ({ ...f, targetDate: e.target.value }))
              }
            />
          </label>

          <label className="er-field er-wide">
            <span>Details</span>
            <textarea
              placeholder="Optional"
              value={goalForm.description}
              onChange={(e) =>
                setGoalForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </label>

          <div className="er-goal-actions er-wide">
            <label className="er-check">
              <input
                type="checkbox"
                checked={goalForm.shared}
                onChange={(e) =>
                  setGoalForm((f) => ({ ...f, shared: e.target.checked }))
                }
              />
              Share with admin
            </label>

            <button className="er-btn" onClick={addGoal}>
              Add Goal
            </button>
          </div>
        </div>

        {data.goals.length === 0 ? (
          <div className="er-empty">No goals yet.</div>
        ) : (
          data.goals.map((g) => (
            <article className="er-goal" key={g._id}>
              <div className="er-goal-top">
                <div className="er-goal-main">
                  <strong>{g.title}</strong>
                  {g.description && <p className="er-text">{g.description}</p>}

                  <div className="er-goal-meta">
                    <em className={`er-status ${g.status}`}>
                      {g.status === "achieved" ? "Achieved" : "Pending"}
                    </em>
                    <span className="er-muted">
                      {g.targetDate
                        ? `Target: ${g.targetDate}`
                        : "No target date"}
                    </span>
                    <span className={`er-share ${g.shared ? "on" : ""}`}>
                      {g.shared ? "Shared with admin" : "Private"}
                    </span>
                  </div>
                </div>

                <div className="er-goal-buttons">
                  <button
                    className="er-btn er-btn-small er-btn-light"
                    onClick={() =>
                      updateGoal(
                        g._id,
                        { shared: !g.shared },
                        g.shared
                          ? "The goal is private now."
                          : "The goal is shared with admin.",
                      )
                    }
                  >
                    {g.shared ? "Stop sharing" : "Share with admin"}
                  </button>

                  <button
                    className="er-btn er-btn-small"
                    onClick={() =>
                      updateGoal(
                        g._id,
                        {
                          status:
                            g.status === "achieved" ? "pending" : "achieved",
                        },
                        g.status === "achieved"
                          ? "The goal is back to pending."
                          : "Goal marked as achieved.",
                      )
                    }
                  >
                    {g.status === "achieved" ? "Reopen" : "Mark achieved"}
                  </button>

                  <button className="er-del" onClick={() => removeGoal(g._id)}>
                    Delete
                  </button>
                </div>
              </div>

              {/* ----- reply of admin / HR ----- */}
              {g.adminReply && (
                <div className="er-bubble er-bubble-mgmt">
                  <div className="er-bubble-head">
                    <b>Reply from {g.repliedByName || "management"}</b>
                    {g.repliedAt && (
                      <span className="er-muted">{fmtDate(g.repliedAt)}</span>
                    )}
                  </div>
                  <p>{g.adminReply}</p>
                </div>
              )}
            </article>
          ))
        )}
      </section>
    </div>
  );
}
